import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useStaticQuery, graphql } from "gatsby";
import { useDispatch } from "react-redux";
import classNames from "classnames";
import CloseIcon from "@images/alert-bar/close.svg";
import * as styles from "./AlertBar.module.scss";

const STORAGE_KEY = "gomes_alert_bar_dismissed";

// Per-property palette applied via inline CSS variables. Lives here in JS
// (not the SCSS module) because Webpack's tree-shaking of namespace imports
// from CSS modules dropped the theme_* class exports in the production
// build, leaving the bar stuck on the default tan fallback.
const THEME_PALETTES = {
  envy: { bg: "#6da7af", text: "#1c2f3f" },
  astor: { bg: "#c3b382", text: "#3e806b" },
  motto: { bg: "#c3b382", text: "#3e806b" },
  nova: { bg: "#c3b382", text: "#3e806b" },
  olea: { bg: "#c3b382", text: "#3e806b" },
  vida: { bg: "#c3b382", text: "#3e806b" },
  sofia: { bg: "#c2b280", text: "#3e806b" },
};

const isFilled = data =>
  Boolean(
    data?.alert_bar_enabled &&
      ((data.alert_bar_heading && data.alert_bar_heading.trim()) ||
        (data.alert_bar_body && data.alert_bar_body.trim()) ||
        (data.alert_bar_cta_text && data.alert_bar_cta_text.trim()))
  );

const matchesSlug = (propertyNode, propertySlug) => {
  const url = propertyNode?.data?.property_link?.url;
  if (!url || !propertySlug) return false;
  return url.replace(/\/$/, "").endsWith(`/${propertySlug}`);
};

// Identifier for "this version of the alert". Prefer Prismic doc id +
// last_publication_date so any republish of the alert re-shows the bar
// even within an existing session. Falls back to a content hash so the
// dev preview (and any non-Prismic content) still has a stable key.
const buildVersionKey = (content, source) => {
  if (source?.prismicId) {
    return `${source.prismicId}@${source.publicationDate || ""}`;
  }
  return [
    "content",
    content.alert_bar_heading || "",
    content.alert_bar_body || "",
    content.alert_bar_cta_text || "",
    content.alert_bar_cta_link?.url || "",
  ].join("|");
};

const isInPageContact = url => {
  if (!url) return false;
  return url === "#contact" || url.endsWith("/#contact");
};

const AlertBar = ({ propertySlug }) => {
  const dispatch = useDispatch();
  const data = useStaticQuery(graphql`
    query AlertBarQuery {
      prismicGomesHomepageGlobal {
        prismicId
        last_publication_date
        data {
          alert_bar_enabled
          alert_bar_heading
          alert_bar_body
          alert_bar_cta_text
          alert_bar_cta_link {
            url
            target
          }
        }
      }
      allPrismicProperty {
        nodes {
          prismicId
          last_publication_date
          data {
            property_link {
              url
            }
            alert_bar_override
            alert_bar_enabled
            alert_bar_heading
            alert_bar_body
            alert_bar_cta_text
            alert_bar_cta_link {
              url
              target
            }
          }
        }
      }
    }
  `);

  const { content, source } = useMemo(() => {
    const globalNode = data?.prismicGomesHomepageGlobal;
    const global = globalNode?.data;
    const propertyNode = propertySlug
      ? data?.allPrismicProperty?.nodes?.find(node =>
          matchesSlug(node, propertySlug)
        )
      : null;
    const override = propertyNode?.data;

    // When the property toggles `alert_bar_override`, it takes full control
    // for that property — including the decision to hide the bar (override
    // on + enabled off = no bar on this property, even if a global is set).
    if (override?.alert_bar_override) {
      if (isFilled(override)) {
        return {
          content: override,
          source: {
            prismicId: propertyNode.prismicId,
            publicationDate: propertyNode.last_publication_date,
          },
        };
      }
      return { content: null, source: null };
    }
    if (isFilled(global)) {
      return {
        content: global,
        source: {
          prismicId: globalNode.prismicId,
          publicationDate: globalNode.last_publication_date,
        },
      };
    }

    // TODO(remove once Prismic schema + content is set up):
    // local fallback so the bar can be previewed in dev before the
    // alert_bar_* fields exist on the live Prismic doc.
    // eslint-disable-next-line no-undef
    if (process.env.GATSBY_ALERT_BAR_PREVIEW === "true") {
      return {
        content: {
          alert_bar_enabled: true,
          alert_bar_heading: "ONE MONTH FREE",
          alert_bar_body:
            "Up to one month free on select apartments for immediate move ins.",
          alert_bar_cta_text: "SCHEDULE A TOUR TODAY",
          alert_bar_cta_link: { url: "#contact", target: null },
        },
        source: null,
      };
    }
    return { content: null, source: null };
  }, [data, propertySlug]);

  const versionKey = content ? buildVersionKey(content, source) : null;
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    if (!content) return;
    if (typeof window === "undefined") return;

    let stored = null;
    try {
      stored = window.sessionStorage.getItem(STORAGE_KEY);
    } catch (e) {
      // sessionStorage may be unavailable (privacy mode); treat as not dismissed
    }
    setDismissed(stored === versionKey);
  }, [content, versionKey]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const visible = Boolean(content) && !dismissed;
    const setOffset = () => {
      if (!visible) {
        document.documentElement.style.removeProperty("--alert-bar-offset");
        return;
      }
      const isMobile = window.matchMedia("(max-width: 1023px)").matches;
      document.documentElement.style.setProperty(
        "--alert-bar-offset",
        isMobile ? "64px" : "40px"
      );
    };
    setOffset();
    window.addEventListener("resize", setOffset);
    return () => {
      window.removeEventListener("resize", setOffset);
      document.documentElement.style.removeProperty("--alert-bar-offset");
    };
  }, [content, dismissed]);

  if (!content || dismissed) return null;

  const handleDismiss = () => {
    try {
      window.sessionStorage.setItem(STORAGE_KEY, versionKey);
    } catch (e) {
      // ignore
    }
    setDismissed(true);
  };

  const ctaText = content.alert_bar_cta_text;
  const ctaUrl = content.alert_bar_cta_link?.url;
  const ctaTarget = content.alert_bar_cta_link?.target;
  const palette = propertySlug ? THEME_PALETTES[propertySlug] : null;
  const themeStyle = palette
    ? { "--alert-bar-bg": palette.bg, "--alert-bar-text": palette.text }
    : undefined;

  return (
    <div
      className={styles.bar}
      style={themeStyle}
      role="region"
      aria-label="Site notice"
    >
      <div className={styles.inner}>
        {content.alert_bar_heading && (
          <span className={styles.heading}>{content.alert_bar_heading}</span>
        )}
        {content.alert_bar_body && (
          <span className={styles.body}>{content.alert_bar_body}</span>
        )}
        {ctaText && ctaUrl && (
          <a
            className={classNames(styles.cta)}
            href={ctaUrl}
            target={ctaTarget || undefined}
            rel={ctaTarget === "_blank" ? "noopener noreferrer" : undefined}
            onClick={(e) => {
              if (!isInPageContact(ctaUrl)) return;
              // CTA points at the in-page contact form: preselect the
              // "take a tour" option and smooth-scroll, mirroring the
              // floating Take A Tour button.
              e.preventDefault();
              dispatch({ type: "SET_CONTACT_TOUR" });
              const target = document.getElementById("contact");
              if (target) {
                target.scrollIntoView({ behavior: "smooth", block: "start" });
                if (window.history && window.history.replaceState) {
                  window.history.replaceState(null, "", "#contact");
                }
              } else {
                window.location.hash = "#contact";
              }
            }}
          >
            {ctaText}
          </a>
        )}
      </div>
      <button
        type="button"
        className={styles.close}
        aria-label="Dismiss notice"
        onClick={handleDismiss}
      >
        <CloseIcon aria-hidden="true" />
      </button>
    </div>
  );
};

AlertBar.propTypes = {
  propertySlug: PropTypes.string,
};

AlertBar.defaultProps = {
  propertySlug: null,
};

export default AlertBar;
